import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import {
  UNSAFE_NavigationContext,
  useLocation,
} from 'react-router-dom';

export type NavigationGuardConfirmation = (
  proceed: () => void,
  stay: () => void,
) => void;

interface RegisteredNavigationGuard {
  confirmation: NavigationGuardConfirmation;
  token: symbol;
}

interface PendingPopNavigation {
  currentIndex: number;
  targetIndex: number;
}

interface NavigationGuardContextValue {
  registerGuard: (confirmation: NavigationGuardConfirmation) => () => void;
}

const NavigationGuardContext = createContext<
  NavigationGuardContextValue | undefined
>(undefined);

const getHistoryIndex = (state: unknown): number | undefined => {
  if (!state || typeof state !== 'object' || !('idx' in state)) {
    return undefined;
  }

  const { idx } = state as { idx?: unknown };
  return typeof idx === 'number' ? idx : undefined;
};

const NavigationGuardProvider = ({ children }: PropsWithChildren) => {
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const location = useLocation();
  const guardRef = useRef<RegisteredNavigationGuard | undefined>(undefined);
  const confirmationPendingRef = useRef(false);
  const currentHistoryIndexRef = useRef(
    getHistoryIndex(window.history.state),
  );
  const restoringPopRef = useRef<PendingPopNavigation | undefined>(undefined);
  const allowedPopIndexRef = useRef<number | undefined>(undefined);

  useLayoutEffect(() => {
    currentHistoryIndexRef.current = getHistoryIndex(window.history.state);
  }, [location.key]);

  const registerGuard = useCallback(
    (confirmation: NavigationGuardConfirmation) => {
      const token = Symbol('navigation-guard');
      guardRef.current = { confirmation, token };

      return () => {
        if (guardRef.current?.token === token) {
          guardRef.current = undefined;
        }
      };
    },
    [],
  );

  const requestConfirmation = useCallback((proceed: () => void) => {
    const guard = guardRef.current;
    if (!guard) {
      proceed();
      return;
    }
    if (confirmationPendingRef.current) return;

    confirmationPendingRef.current = true;
    const settle = (next?: () => void) => {
      confirmationPendingRef.current = false;
      next?.();
    };

    try {
      guard.confirmation(
        () => settle(proceed),
        () => settle(),
      );
    } catch (error) {
      confirmationPendingRef.current = false;
      throw error;
    }
  }, []);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const targetIndex = getHistoryIndex(event.state);
      const currentIndex = currentHistoryIndexRef.current;

      if (!guardRef.current) {
        currentHistoryIndexRef.current = targetIndex;
        restoringPopRef.current = undefined;
        allowedPopIndexRef.current = undefined;
        return;
      }

      if (
        targetIndex !== undefined &&
        allowedPopIndexRef.current === targetIndex
      ) {
        allowedPopIndexRef.current = undefined;
        currentHistoryIndexRef.current = targetIndex;
        return;
      }

      const restoringPop = restoringPopRef.current;
      if (
        restoringPop &&
        targetIndex !== undefined &&
        targetIndex === restoringPop.currentIndex
      ) {
        event.stopImmediatePropagation();
        restoringPopRef.current = undefined;
        requestConfirmation(() => {
          allowedPopIndexRef.current = restoringPop.targetIndex;
          window.history.go(
            restoringPop.targetIndex - restoringPop.currentIndex,
          );
        });
        return;
      }

      if (restoringPop) {
        event.stopImmediatePropagation();
        return;
      }

      // Entries created outside BrowserRouter do not carry its numeric index.
      // Those navigations leave the document and remain covered by beforeunload.
      if (
        currentIndex === undefined ||
        targetIndex === undefined ||
        currentIndex === targetIndex
      ) {
        currentHistoryIndexRef.current = targetIndex;
        return;
      }

      // popstate fires after the browser changes entries. Capture it before
      // BrowserRouter, restore its current entry, then ask before replaying it.
      event.stopImmediatePropagation();
      restoringPopRef.current = { currentIndex, targetIndex };
      window.history.go(currentIndex - targetIndex);
    };

    window.addEventListener('popstate', handlePopState, true);
    return () => window.removeEventListener('popstate', handlePopState, true);
  }, [requestConfirmation]);

  const guardedNavigator = useMemo(
    () => ({
      ...navigationContext.navigator,
      push: (
        ...args: Parameters<typeof navigationContext.navigator.push>
      ) =>
        requestConfirmation(() => navigationContext.navigator.push(...args)),
      replace: (
        ...args: Parameters<typeof navigationContext.navigator.replace>
      ) =>
        requestConfirmation(() =>
          navigationContext.navigator.replace(...args),
        ),
    }),
    [navigationContext, requestConfirmation],
  );

  const guardedNavigationContext = useMemo(
    () => ({ ...navigationContext, navigator: guardedNavigator }),
    [guardedNavigator, navigationContext],
  );
  const guardContext = useMemo(
    () => ({ registerGuard }),
    [registerGuard],
  );

  return (
    <NavigationGuardContext.Provider value={guardContext}>
      <UNSAFE_NavigationContext.Provider value={guardedNavigationContext}>
        {children}
      </UNSAFE_NavigationContext.Provider>
    </NavigationGuardContext.Provider>
  );
};

export const useNavigationGuard = (
  enabled: boolean,
  confirmation: NavigationGuardConfirmation,
) => {
  const context = useContext(NavigationGuardContext);
  if (!context) {
    throw new Error(
      'useNavigationGuard must be used within NavigationGuardProvider',
    );
  }

  useEffect(() => {
    if (!enabled) return undefined;
    return context.registerGuard(confirmation);
  }, [confirmation, context, enabled]);
};

export default NavigationGuardProvider;
